"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getValue = getValue;
exports.getQvetValue = getQvetValue;
exports.hasPending = hasPending;
exports.isDirty = isDirty;
exports.setPending = setPending;
exports.clearPending = clearPending;
exports.confirmSync = confirmSync;
exports.applySyncPreservePending = applySyncPreservePending;
/**
 * Get the effective value (pending if exists, otherwise current)
 *
 * @example
 * getValue({ value: "old", pending: "new" }) // "new"
 * getValue({ value: "old", pending: undefined }) // "old"
 */
function getValue(field) {
    return field.pending ?? field.value;
}
/**
 * Get the external system value (always the synced one, ignores pending)
 *
 * @example
 * getQvetValue({ value: "old", pending: "new" }) // "old"
 */
function getQvetValue(field) {
    return field.value;
}
/**
 * Check if field has a pending change
 *
 * @example
 * hasPending({ value: "old", pending: "new" }) // true
 * hasPending({ value: "old", pending: undefined }) // false
 */
function hasPending(field) {
    return field.pending !== undefined && field.pending !== null;
}
/**
 * Check if pending value differs from current value
 *
 * @example
 * isDirty({ value: "old", pending: "new" }) // true
 * isDirty({ value: "old", pending: "old" }) // false (same value)
 * isDirty({ value: "old", pending: undefined }) // false
 */
function isDirty(field) {
    return hasPending(field) && field.pending !== field.value;
}
/**
 * Set a pending value. If newValue equals current value, clears pending instead.
 *
 * @example
 * setPending({ value: "old", pending: undefined }, "new")
 * // { value: "old", pending: "new" }
 *
 * setPending({ value: "old", pending: undefined }, "old")
 * // { value: "old", pending: undefined } - no change needed
 */
function setPending(field, newValue) {
    if (newValue === field.value) {
        return { value: field.value, pending: undefined };
    }
    return { value: field.value, pending: newValue };
}
/**
 * Clear pending (revert to external system value)
 *
 * @example
 * clearPending({ value: "old", pending: "new" })
 * // { value: "old", pending: undefined }
 */
function clearPending(field) {
    return { value: field.value, pending: undefined };
}
/**
 * Confirm sync from external system.
 * Updates the value to the new external value and clears pending.
 *
 * @example
 * // External system confirmed our change
 * confirmSync({ value: "old", pending: "new" }, "new")
 * // { value: "new", pending: undefined }
 *
 * // External system has different value
 * confirmSync({ value: "old", pending: undefined }, "updated")
 * // { value: "updated", pending: undefined }
 */
function confirmSync(field, externalValue) {
    return { value: externalValue, pending: undefined };
}
/**
 * Apply sync from external system, preserving pending if it differs from new value.
 * Use this when you want to keep local changes during a sync.
 *
 * @example
 * // We have pending, external hasn't changed yet
 * applySyncPreservePending({ value: "old", pending: "new" }, "old")
 * // { value: "old", pending: "new" } - pending preserved
 *
 * // External confirmed our change
 * applySyncPreservePending({ value: "old", pending: "new" }, "new")
 * // { value: "new", pending: undefined } - pending cleared
 *
 * // External changed to something else (conflict scenario)
 * applySyncPreservePending({ value: "old", pending: "new" }, "other")
 * // { value: "other", pending: "new" } - pending preserved, value updated
 */
function applySyncPreservePending(field, externalValue) {
    // If pending equals new external value, the change was confirmed
    if (field.pending === externalValue) {
        return { value: externalValue, pending: undefined };
    }
    // Otherwise, update value but preserve pending
    return { value: externalValue, pending: field.pending };
}
